/**
 * Shared action row for admin add/edit forms. Renders directly BELOW the form
 * card (never inside it) so every add/edit page places Save/Cancel the same way.
 * Matches the Edit Franchise Partner reference: centred, size large, a primary
 * teal Save button + a secondary Cancel text button.
 *
 * Destructive actions (delete / kebab) belong in the page header, not here.
 */
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Button, CircularProgress } from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import { poolwerxColors } from '../../../theme/colors'

export interface AdminFormActionsProps {
  /** Primary button label when idle, e.g. "Save Changes" / "Create User". */
  saveLabel: string
  /** True while the submit is in flight - shows a spinner + "Saving...". */
  saving: boolean
  /** Extra disable conditions for Save beyond `saving` (loading, read-only). */
  saveDisabled?: boolean
  /** Save click handler. Omit when the button submits a wrapping <form>. */
  onSave?: () => void
  /** Render Save as type="submit" (pages that wrap this in <form onSubmit>). */
  submit?: boolean
  /** Cancel as a router link (supports open-in-new-tab)... */
  cancelTo?: string
  /** ...or an onClick handler (e.g. an unsaved-changes guard). */
  onCancel?: () => void
  cancelDisabled?: boolean
}

const AdminFormActions: React.FC<AdminFormActionsProps> = ({
  saveLabel,
  saving,
  saveDisabled = false,
  onSave,
  submit = false,
  cancelTo,
  onCancel,
  cancelDisabled = false,
}) => (
  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
    <Button
      type={submit ? 'submit' : 'button'}
      onClick={submit ? undefined : onSave}
      variant="contained"
      size="large"
      disabled={saving || saveDisabled}
      startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
      sx={{ bgcolor: poolwerxColors.teal, '&:hover': { bgcolor: poolwerxColors.teal, opacity: 0.9 } }}
    >
      {saving ? 'Saving...' : saveLabel}
    </Button>
    {cancelTo ? (
      <Button component={RouterLink} to={cancelTo} size="large" disabled={saving || cancelDisabled}>
        Cancel
      </Button>
    ) : (
      <Button onClick={onCancel} size="large" disabled={saving || cancelDisabled}>
        Cancel
      </Button>
    )}
  </Box>
)

export default AdminFormActions
